<template>
  <q-btn-dropdown
    :label="label"
    :color="color"
    outline
    :loading="isLoading"
  >
    <div v-if="isInvited" class="q-pa-md">
      <p>{{ userType }} has been emailed an invite to activate their account.</p>
      <p>Once their account is activated they can login to the app.</p>
      <q-separator />
      <div class="text-center q-mt-md q-gutter-sm">
        <q-btn
          icon="fa fa-share"
          label="Resend Invite"
          color="primary"
          class="q-mb-sm"
          @click="resendInvite" />
        <CopyInviteLinkBtn v-if="isClient" :user-id="userId" class="q-pt-xl"/>
      </div>
      <div v-if="canToggleReminder">
          <q-separator class="q-mt-md" />
          <setup-reminder-toggle
            :user="user"
            @update:user="$emit('update:user', $event)"
            class="q-py-md"
          />
      </div>
    </div>
    <div v-else class="q-pa-md">
      {{ userType }} has activated their account and can login to the app.
    </div>
  </q-btn-dropdown>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import isGranted from '@/auth/isGranted';
import { User } from '@/type';
import { daysSince } from '@/util/date';
import { HasTaskMixin } from '@/mixins';
import notify from '@/components/notify';
import { isInvited, resendInvite } from './user';
import CopyInviteLinkBtn from './CopyInviteLinkBtn.vue';
import SetupReminderToggle from './SetupReminderToggle.vue';

const statuses = ['Inactive', 'Active', 'Invited'];

const colors = ['danger', 'positive', 'warning'];

interface Data {
  task: Promise<void>|null,
}

export default defineComponent({
  emits: ['update:user'],
  mixins: [HasTaskMixin],
  components: {
    CopyInviteLinkBtn,
    SetupReminderToggle,
  },
  props: {
    userType: {
      type: String,
      default: 'User',
    },
    user: {
      type: Object as () => User,
      required: true,
    },
  },
  data(): Data {
    return {
      task: null,
    };
  },
  computed: {
    userId(): number {
      return this.user.id;
    },
    value(): number {
      return this.user.status;
    },
    isClient(): boolean {
      return this.userType.toLowerCase() === 'client';
    },
    isInvited(): boolean {
      return isInvited(this.value);
    },
    color(): string {
      return colors[this.value];
    },
    label(): string {
      return statuses[this.value];
    },
    isPastSetupPeriod(): boolean {
      return daysSince(this.user.createdAt) > 14;
    },
    canToggleReminder(): boolean {
      return (
        isGranted('TOGGLE_USER_SETUP_REMINDER')
        && this.isClient
        && !this.isPastSetupPeriod
      );
    },
  },
  methods: {
    resendInvite() {
      this.task = resendInvite(this.userId);
      this.task.then(() => {
        notify('The invitation has been sent.');
      });
    },
  },
});
</script>
